import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { cssColor } from '../colors';
import type { Role, UserCreateBody, UserResponse } from '../types';
import { Modal } from './Modal';

export interface UserCreateDialogProps {
  roles: Role[];
  onClose: () => void;
  onCreated: (message: string) => void;
}

/** Neuen Benutzer anlegen. */
export function UserCreateDialog({ roles, onClose, onCreated }: UserCreateDialogProps): ReactNode {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (username.trim() === '') {
      setError('Ein Benutzername ist erforderlich.');
      return;
    }
    if (password !== '' && password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    setBusy(true);
    setError(null);

    const body: UserCreateBody = { username: username.trim() };
    if (displayName.trim() !== '') body.displayName = displayName.trim();
    if (password !== '') body.password = password;
    if (roleId !== '') body.roleId = roleId;

    try {
      const result = await api.post<UserResponse>('/api/users', body);
      onCreated(`Benutzer "${result.user?.username ?? username.trim()}" angelegt.`);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  };

  return (
    <Modal title="Neuer Benutzer" onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <label className="field">
          <span>Benutzername *</span>
          <input
            type="text"
            value={username}
            autoFocus
            placeholder="mc_spieler"
            onChange={(event) => setUsername(event.target.value)}
          />
          <span className="field-hint">
            3–64 Zeichen: Buchstaben, Zahlen, Punkt, Unterstrich, Bindestrich
          </span>
        </label>

        <label className="field">
          <span>Anzeigename</span>
          <input
            type="text"
            value={displayName}
            placeholder="Leer lassen = Benutzername"
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Passwort (optional, min. 8 Zeichen)</span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Rolle</span>
          <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
            <option value="">Keine Rolle</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.displayName} ({role.name})
              </option>
            ))}
          </select>
        </label>

        {roleId !== '' && (
          <span className="role-preview">
            <span
              className="color-dot"
              style={{ backgroundColor: cssColor(roles.find((role) => role.id === roleId)?.color) }}
            />
            Rolle wird beim Anlegen als manuell gesetzt markiert (kein Cloud-Überschreiben).
          </span>
        )}

        {error !== null && (
          <div className="feedback feedback-error" role="alert">
            <span className="feedback-icon" aria-hidden="true">
              !
            </span>
            <span>{error}</span>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="button button-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? 'Anlegen …' : 'Benutzer anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
